import React, { useEffect, useMemo, useState } from "react"
import { Card, InputNumber, Table, Button, message, Tooltip } from "antd"
import { DeleteOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { secureApi } from "../../Config/api"
import { POST } from "../../utils/apis"
import { fetchEstimations, fetchStats, shortText } from "../../utils/helpers"
import { translations } from "../../utils/constants"

export default function EstimationTab({ vehicleId, language }) {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.authReducer?.user)
  const estimationData = useSelector((state) => state.estimation.data)
  const estimationSummary = useSelector((state) => state.estimation.summary);
  const loading = useSelector((state) => state.estimation.loading)

  useEffect(() => {
    if (user && vehicleId) {
      fetchEstimations({ user, dispatch, vehicleId })
    }
  }, [user, dispatch, vehicleId])

  const { systems, components } = useMemo(() => {
    const systemsMap = new Map()
    const componentsMap = new Map()

    estimationData?.forEach(r => {
      if (!systemsMap.has(r.device_id)) {
        systemsMap.set(r.device_id, {
          id: r.device_id,
          name: r.device_name,
          color: r.device_color,
          estimationId: r.device_quantity_id
        })
      }

      if (!componentsMap.has(r.unit_id)) {
        componentsMap.set(r.unit_id, {
          id: r.unit_id,
          name: r.unit_name,
          unitPrice: parseFloat(r.unit_price) || 0,
          quantities: {}
        })
      }

      const comp = componentsMap.get(r.unit_id)
      comp.quantities[r.device_id] = r.quantity ? parseFloat(r.quantity) : 0
    })

    return {
      systems: Array.from(systemsMap.values()),
      components: Array.from(componentsMap.values())
    }
  }, [estimationData])

  const updateQuantity = async (compId, sysId, value) => {
    try {
      const qty = value || 0
      await secureApi.post(POST.UPDATE_DEVICE_QUANTITY, {
        vehicle_id: vehicleId,
        device_id: sysId,
        unit_id: compId,
        quantity: qty
      })
      fetchEstimations({ user, dispatch, vehicleId })
      fetchStats({ dispatch, user, vehicleId })
    } catch (err) {
      message.error(translations[language]?.updateEstimationError || "Failed to update quantity")
    }
  }

  const updatePrice = async (compId, value) => {
    try {
      const updatedPrice = parseFloat(value) || 0

      await secureApi.post(POST?.UPDATE_UNIT_PRICE, {
        vehicle_id: vehicleId,
        unit_id: compId,
        new_price: updatedPrice
      })
      fetchEstimations({ user, dispatch, vehicleId })
      fetchStats({ dispatch, user, vehicleId })
    } catch {
      message.error(translations[language]?.updatePriceError || "Failed to update price")
    }
  }

  const deleteComponent = async (id) => {
    try {
      const res = await secureApi?.post(POST.DELETE_UNIT, {
        vehicle_id: vehicleId,
        unit_id: id
      })
      if (res?.success) {
        message.success(translations[language]?.componentDeleted)
        fetchEstimations({ user, dispatch, vehicleId })
        fetchStats({ dispatch, user, vehicleId })
      } else {
        message.error(res?.message || translations[language]?.deleteComponentError)
      }
    } catch {
      message.error(translations[language]?.deleteComponentError)
    }
  }

  const columns = [
    {
      title: translations[language]?.units,
      dataIndex: "name",
      key: "name",
      summaryRender: () => <strong>{translations[language].total}</strong>
    },
    ...(user?.role !== "user"
      ? [
        {
          title: translations[language]?.unitPrice,
          dataIndex: "unitPrice",
          render: (val, record) => (
            <>€ <InputNumber min={0} value={val} onChange={(v) => updatePrice(record.id, v)} /></>
          ),
          summaryRender: () => "-"
        }
      ]
      : []),

    ...systems.map(sys => ({
      title: (
        <Tooltip title={sys.name}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: sys.color
            }}
            />
            {shortText(sys.name, 15)}
          </span>
        </Tooltip>
      ),
      render: (_, record) => (
        <InputNumber
          precision={0}
          min={0}
          value={record?.quantities[sys?.id] || 0}
          onChange={(v) => updateQuantity(record?.id, sys.id, v)}
        />
      ),
      summaryRender: () => {
        const device = estimationSummary?.deviceTotals?.[sys.name];
        // const qty = Number(device?.totalQty || 0);
        const qty = Math.round(Number(device?.totalQty || 0));

        return (
          <span style={{ fontWeight: "bold", color: "red" }}>
            {qty}
          </span>
        );
      }
    })),

    ...(user?.role !== "user"
      ? [
        {
          title: translations[language]?.total,
          fixed: "right",
          render: (_, rec) => {
            const total = systems.reduce(
              (sum, s) => sum + (rec.quantities[s.id] || 0) * (rec.unitPrice || 0),
              0
            )
            return <span style={{ fontWeight: "bold", color: "red" }}>€{total.toFixed(2)}</span>
          },
          summaryRender: () => (
            <span style={{ fontWeight: "bold", color: "green" }}>
              €{(estimationSummary?.grandTotal || 0).toFixed(2)}
            </span>
          )
        },
        {
          title: translations[language]?.action,
          fixed: "right",
          render: (_, record) => (
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => deleteComponent(record.id)} />
          ),
          summaryRender: () => "-"
        }
      ]
      : [])
  ]

  return (
    <div>
      <Card
        title={translations[language]?.unitsQuantitiesCosts}
        bordered={false}
        className="rounded-card"
      >
        <p style={{ color: "#666", marginBottom: 12 }}>
          {translations[language]?.manageQuantitiesSubtitle}
        </p>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={components}
          scroll={{ x: "max-content" }}
          summary={() => (
            <Table.Summary.Row>
              {columns.map((col, idx) => (
                <Table.Summary.Cell key={idx} index={idx}>
                  {col.summaryRender ? col.summaryRender() : null}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          )}
        />

      </Card>
    </div>
  )
}